<script setup lang="ts">
import Typography from '@components/Typography.vue';
import UserPicture from '@components/UserPicture.vue';
import { eventBus } from '@tools/event-bus';
import { URL_PUBLISH_OFFER, URL_USER_ORGANIZATION_TEAM } from '@elements/tools/routes/routes';
import { trans } from '@elements/translation';
import type { MenuItem } from '@global-types/nav/menuItem';
import { useUserProfile } from '@composables/useUserProfile';
import { UserAuth } from '@tools/user/auth';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import IconPlus from '@icons/ph_plus.svg';
import IconUsersPlus from '@icons/ph_users-plus.svg';
import AccountManagerMenu from '@layout/nav/user/AccountManagerMenu.vue';
import { ACCOUNT_PROFILE } from '@layout/nav/config';
import { useNavbarMenu } from '@layout/nav/user/useNavbarMenu';
import DesktopVerticalMenuButton from './DesktopVerticalMenu/DesktopVerticalMenuButton.vue';
import DesktopVerticalMenuLink from './DesktopVerticalMenu/DesktopVerticalMenuLink.vue';
import DesktopVerticalMenuTooltip from './DesktopVerticalMenu/DesktopVerticalMenuTooltip.vue';
import SidebarSubMenu from './SidebarSubMenu.vue';
import UserMenu from './UserMenu.vue';

const isUserMenuOpened = ref(false);
const isCollapsed = ref<boolean>(!!window.SIDEBAR_COLLAPSED);
const pageWrapper = document.getElementById('page-wrapper') as HTMLDivElement;

const {
  user,
  userProfile,
  isProfileManagement,
  hasKYCActive,
  hasPayBuyer,
  hasPaySeller,
  switchProfileType,
} = useUserProfile();

const {
  isCurrentPage,
  menuItems,
  isMenuOpen,
  toggleMenu,
  resetMenu,
  isCurrentCategory,
  getNotificationsCount,
} = useNavbarMenu();

const closeUserMenu = () => {
  isUserMenuOpened.value = false;
};

const openUserMenu = () => {
  if (isUserMenuOpened.value) {
    closeUserMenu();
    return;
  }

  isUserMenuOpened.value = true;
};

const handleToggleMenu = (menuItem: MenuItem): void => {
  toggleMenu(menuItem.name);
  closeUserMenu();
};

const handleCloseSidebar = () => {
  isCollapsed.value = true;
  pageWrapper.classList.remove('page-wrapper-sidebar');
  pageWrapper.classList.add('page-wrapper-sidebar--mini');
};

const handleSidebarFixed = (args: CustomEvent): void => {
  const { value } = args.detail;

  if (value) {
    pageWrapper.classList.add('page-wrapper-sidebar--overflow');
    pageWrapper.classList.remove(
      'page-wrapper-sidebar',
      'page-wrapper-sidebar--mini',
      'page-wrapper-sidebar--over',
    );

    isCollapsed.value = true;
    return;
  }

  pageWrapper.classList.remove('page-wrapper-sidebar--overflow');
  pageWrapper.classList.add(isCollapsed.value ? 'page-wrapper-sidebar--mini' : 'page-wrapper-sidebar');
};

const menuBottomItems = ref([
  {
    href: URL_PUBLISH_OFFER,
    icon: IconPlus,
    iconName: 'ph_plus',
    label: 'user_menu.main.create_offer',
    profiles: [ ACCOUNT_PROFILE.NO_PROFILE ],
  },
  {
    href: URL_USER_ORGANIZATION_TEAM,
    icon: IconUsersPlus,
    iconName: 'ph_users-plus',
    label: 'user_menu.main.add_user',
    profiles: [ ACCOUNT_PROFILE.BUYER, ACCOUNT_PROFILE.SELLER, ACCOUNT_PROFILE.BOTH ],
  },
]);

const activeMenuBottomItem = computed(() => menuBottomItems.value.find(
  (item) => item.profiles.includes(userProfile.value),
));

const initSidebarButton = () => {
  eventBus.dispatchEvent(new Event('open-side-bar-button'));
  const sidebarBtn = document.getElementById('sidebar-btn') as HTMLButtonElement | null;
  sidebarBtn?.addEventListener('click', () => {
    isCollapsed.value = !isCollapsed.value;
  });
};

initSidebarButton();

const handleSidebarCollapsed = () => {
  if (isCollapsed.value) {
    pageWrapper.classList.remove('page-wrapper-sidebar');
    pageWrapper.classList.add('page-wrapper-sidebar--mini');
    return;
  }

  pageWrapper.classList.add('page-wrapper-sidebar');
  pageWrapper.classList.remove('page-wrapper-sidebar--mini');

  eventBus.dispatchEvent(new Event('inbox-close-right-block'));
};

const handleSidebarOverflow = () => {
  if (isCollapsed.value) {
    pageWrapper.classList.add('page-wrapper-sidebar');
    pageWrapper.classList.remove('page-wrapper-sidebar--overflow');
    return;
  }

  pageWrapper.classList.remove('page-wrapper-sidebar--overflow');
  pageWrapper.classList.add('page-wrapper-sidebar');
};

watch(isCollapsed, () => {
  handleSidebarCollapsed();

  if (!window.SIDEBAR_OVERFLOW) {
    handleSidebarOverflow();
  }

  if (isCollapsed.value) {
    resetMenu();
  }
});

onMounted(() => {
  eventBus.addEventListener('close-sidebar', handleCloseSidebar);
  eventBus.addEventListener('sidebar-fixed', handleSidebarFixed);

  menuItems.value.forEach((menuItem) => {
    if (isCurrentCategory(menuItem)) {
      handleToggleMenu(menuItem);
    }
  });
  if (window.SIDEBAR_COLLAPSED) {
    handleSidebarCollapsed();
  }
});

onUnmounted(() => {
  eventBus.removeEventListener('close-sidebar', handleCloseSidebar);
  eventBus.removeEventListener('sidebar-fixed', handleSidebarFixed);
});

const isImpersonated = computed<boolean>(() => UserAuth.isImpersonated());
</script>

<template>
  <div
    class="side-bar-menu"
    :class="{ 'side-bar-menu__collapsed': isCollapsed, 'side-bar-menu-with-banner': isImpersonated }"
  >
    <ul class="side-bar-menu__list">
      <li
        v-for="menuItem in menuItems"
        :key="menuItem.name"
        ref="menuRef"
        class="side-bar-menu__item"
        :class="menuItem.type"
      >
        <template v-if="menuItem.submenus?.length">
          <DesktopVerticalMenuButton
            v-if="menuItem.icon && menuItem.submenus"
            v-show="!isCollapsed"
            :icon="menuItem.icon"
            :icon-name="menuItem.iconName"
            :is-new="menuItem.isNew"
            :submenus="menuItem.submenus"
            :is-open="isMenuOpen(menuItem.name)"
            :is-current-category="isCurrentCategory(menuItem)"
            :is-collapsed="isCollapsed"
            @click="handleToggleMenu(menuItem)"
          >
            {{ menuItem.name }}
          </DesktopVerticalMenuButton>

          <DesktopVerticalMenuTooltip v-show="isCollapsed">
            <template #trigger>
              <DesktopVerticalMenuButton
                v-if="menuItem.icon"
                :icon="menuItem.icon"
                :icon-name="menuItem.iconName"
                :is-new="menuItem.isNew"
                :submenus="menuItem.submenus"
                :is-open="isMenuOpen(menuItem.name)"
                :is-current-category="isCurrentCategory(menuItem)"
                :is-collapsed="isCollapsed"
              >
                {{ menuItem.name }}
              </DesktopVerticalMenuButton>
            </template>

            <template #panel>
              <SidebarSubMenu
                :is-collapsed="isCollapsed"
                :submenus="menuItem.submenus"
                :is-profile-management="isProfileManagement"
                :has-kyc-active="hasKYCActive"
                :has-pay-buyer="hasPayBuyer"
                :has-pay-seller="hasPaySeller"
                :menu-name="menuItem.name"
              />
            </template>
          </DesktopVerticalMenuTooltip>

          <SidebarSubMenu
            v-if="isMenuOpen(menuItem.name) && !isCollapsed"
            :is-open="isMenuOpen(menuItem.name)"
            :submenus="menuItem.submenus"
            :is-profile-management="isProfileManagement"
            :has-kyc-active="hasKYCActive"
            :has-pay-buyer="hasPayBuyer"
            :has-pay-seller="hasPaySeller"
            :menu-name="menuItem.name"
          />
        </template>

        <template v-else-if="menuItem.link && (!menuItem.hideOnBothProfile || menuItem.hideOnBothProfile && userProfile !== ACCOUNT_PROFILE.BOTH)">
          <DesktopVerticalMenuLink
            v-show="!isCollapsed"
            :link="menuItem.link"
            :icon="menuItem.icon"
            :icon-name="menuItem.iconName"
            :has-icon-stroke="menuItem.hasIconStroke"
            :is-active="isCurrentPage(menuItem.link)"
            :is-open="isMenuOpen(menuItem.name)"
            :is-collapsed="isCollapsed"
            :notifications-count="getNotificationsCount(menuItem.notification)"
          >
            {{ menuItem.name }}
          </DesktopVerticalMenuLink>

          <DesktopVerticalMenuTooltip v-show="isCollapsed">
            <template #trigger>
              <DesktopVerticalMenuLink
                :link="menuItem.link"
                :icon="menuItem.icon"
                :icon-name="menuItem.iconName"
                :has-icon-stroke="menuItem.hasIconStroke"
                :is-active="isCurrentPage(menuItem.link)"
                :is-open="isMenuOpen(menuItem.name)"
                :is-collapsed="isCollapsed"
                :notifications-count="getNotificationsCount(menuItem.notification)"
              >
                {{ menuItem.name }}
              </DesktopVerticalMenuLink>
            </template>

            <template #panel>
              <DesktopVerticalMenuLink
                :link="menuItem.link"
                :is-active="isCurrentPage(menuItem.link)"
                :is-open="isMenuOpen(menuItem.name)"
                :is-collapsed="false"
                :notifications-count="getNotificationsCount(menuItem.notification)"
              >
                {{ menuItem.name }}
              </DesktopVerticalMenuLink>
            </template>
          </DesktopVerticalMenuTooltip>
        </template>

        <hr v-else-if="menuItem.type" :class="menuItem.class">
      </li>
      <li v-show="!isCollapsed">
        <AccountManagerMenu :account-manager="user!.account_manager" />
      </li>
    </ul>

    <div>
      <div v-if="activeMenuBottomItem">
        <DesktopVerticalMenuLink
          v-show="!isCollapsed"
          class="side-bar-menu__title--bottom"
          :link="activeMenuBottomItem.href"
          :icon="activeMenuBottomItem.icon"
          :icon-name="activeMenuBottomItem.iconName"
          :is-collapsed="isCollapsed"
        >
          {{ trans(activeMenuBottomItem.label) }}
        </DesktopVerticalMenuLink>

        <DesktopVerticalMenuTooltip v-show="isCollapsed">
          <template #trigger>
            <DesktopVerticalMenuLink
              class="side-bar-menu__title--bottom"
              :link="activeMenuBottomItem.href"
              :icon="activeMenuBottomItem.icon"
              :icon-name="activeMenuBottomItem.iconName"
              :is-collapsed="isCollapsed"
            >
              {{ trans(activeMenuBottomItem.label) }}
            </DesktopVerticalMenuLink>
          </template>

          <template #panel>
            <DesktopVerticalMenuLink
              class="side-bar-menu__title--bottom"
              :link="activeMenuBottomItem.href"
              :is-collapsed="false"
            >
              {{ trans(activeMenuBottomItem.label) }}
            </DesktopVerticalMenuLink>
          </template>
        </DesktopVerticalMenuTooltip>
      </div>

      <button
        v-if="user && !isCollapsed"
        class="side-bar-button-user"
        @click="openUserMenu"
      >
        <div class="side-bar-button-user__content">
          <UserPicture :user="user" class="side-bar-button-user-picture" :size="32" />
          <p class="side-bar-button-user-infos">
            <Typography variant="body">
              {{ user.firstname }}
            </Typography>
            <Typography
              v-if="user.organization?.name"
              variant="body-small"
              color="gray-darker"
            >
              ({{ user.organization.name }})
            </Typography>
          </p>
        </div>
        <div v-if="isUserMenuOpened && !isCollapsed" class="side-bar-button-user__menu">
          <UserMenu
            :user-profile="userProfile"
            @close="closeUserMenu"
            @change-menu="switchProfileType"
          />
        </div>
      </button>

      <div v-if="user && isCollapsed">
        <UserPicture
          v-if="!isCollapsed"
          :user="user"
          class="side-bar-menu__profile-picture"
          :size="32"
          @click="openUserMenu"
        />

        <DesktopVerticalMenuTooltip panel-class="vertical-menu-tooltip__panel--bottom">
          <template #trigger>
            <UserPicture
              id="profile-picture-desktop"
              :user="user"
              class="side-bar-menu__profile-picture"
              :size="32"
              @click="openUserMenu"
            />
          </template>

          <template #panel>
            <UserMenu
              :is-collapsed="isCollapsed"
              :user-profile="userProfile"
              @close="closeUserMenu"
              @change-menu="switchProfileType"
            />
          </template>
        </DesktopVerticalMenuTooltip>
      </div>
    </div>
  </div>
</template>
